import "./site/error-beacon";
import { Ascent, AscentLaunch, AscentLanding, AscentMode } from "./games/ascent/ascent";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import "./ui/styles.css";

const PB_KEY = "aether.ascent_pb";
const HANDLE_KEY = "aether.handle";

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startAscentRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/ascent", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishAscentRun(runId: string, handle: string, launches: AscentLaunch[], landings: AscentLanding[]) {
  try {
    const res = await fetch(`/api/runs/ascent/${runId}`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, launches, landings }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudHeight = document.getElementById("hud-height")!;
const hudState = document.getElementById("hud-state")!;
const hudScore = document.getElementById("hud-score")!;
const hudGlyphs = document.getElementById("hud-glyphs");
const hudTimeLeft = document.getElementById("hud-time-left");
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const modeToggles = document.querySelectorAll<HTMLButtonElement>(".mode-toggle");
const zoneBanner = document.getElementById("zone-banner") as HTMLElement | null;
let zoneBannerTimer = 0;
function showZoneBanner(name: string, bonus: number) {
  if (!zoneBanner) return;
  zoneBanner.innerHTML = `<div class="ms-title">ENTERING THE ${name.toUpperCase()}</div><div class="ms-sub">+${bonus.toLocaleString()} pts</div>`;
  zoneBanner.classList.add("visible");
  window.clearTimeout(zoneBannerTimer);
  zoneBannerTimer = window.setTimeout(() => zoneBanner.classList.remove("visible"), 2600);
}

// Mode comes from URL `?mode=time` or local toggle.
function readMode(): AscentMode {
  try {
    const url = new URL(window.location.href);
    const m = url.searchParams.get("mode");
    if (m === "time") return "time";
  } catch { /* ignore */ }
  return "endless";
}
let mode: AscentMode = readMode();

function applyModeUI() {
  modeToggles.forEach((btn) => {
    btn.classList.toggle("selected", btn.dataset.mode === mode);
  });
  if (hudTimeLeft) hudTimeLeft.parentElement?.classList.toggle("hidden", mode !== "time");
}
applyModeUI();
modeToggles.forEach((btn) => {
  btn.addEventListener("click", () => {
    mode = btn.dataset.mode === "time" ? "time" : "endless";
    applyModeUI();
  });
});

let game: Ascent | null = null;
let activeRunId: string | null = null;
let pendingPayload: { launches: AscentLaunch[]; landings: AscentLanding[] } | null = null;

async function startRun() {
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startAscentRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Ascent(canvas, {
    onZoneEnter: ({ name, bonus }) => showZoneBanner(name, bonus),
    onHud: (state) => {
      hudHeight.textContent = String(state.height);
      hudState.textContent = state.falling ? "falling" : "—";
      hudScore.textContent = state.scoreEstimate.toLocaleString();
      if (hudGlyphs) hudGlyphs.textContent = String(state.glyphsCollected);
      if (hudTimeLeft && state.timeLeftMs !== null) {
        hudTimeLeft.textContent = (state.timeLeftMs / 1000).toFixed(1);
      }
    },
    onGameOver: async ({ launches, landings, scoreEstimate, height, glyphsCollected, glyphsInReach, bonusPadLandings, mode: runMode }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = runMode === "time" ? "Time up" : "Fell";
      // v5: show count vs. ceiling — "3/5 glyphs" reads as engagement
      // with collectibles vs. how many were actually reachable on this run.
      const glyphTag = glyphsInReach > 0 ? ` · ${glyphsCollected}/${glyphsInReach}✦ glyphs` : "";
      const padTag = bonusPadLandings > 0 ? ` · ${bonusPadLandings} bonus pads` : "";
      msgBody.textContent = `Height ${height} · ${scoreEstimate.toLocaleString()} pts${glyphTag}${padTag}`;
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "ascent", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("ascent", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishAscentRun(activeRunId, handle, launches, landings);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingPayload = { launches, landings };
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.setMode(mode);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = mode === "time" ? "Ascent · Time Trial" : "Ascent";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingPayload) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishAscentRun(activeRunId, value, pendingPayload.launches, pendingPayload.landings);
  pendingPayload = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
